/**
 * GA4 Ecommerce event helpers.
 *
 * All helpers are safe no-ops when:
 *   • window.gtag isn't loaded (dev without a Measurement ID, blocker extensions)
 *   • SSR / build-time (typeof window === 'undefined')
 *
 * Event payloads follow GA4's Enhanced Ecommerce spec:
 *   https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 *
 * Required `items[]` schema (every event):
 *   item_id           — unique course/camp identifier
 *   item_name         — full course/camp name
 *   item_category     — program type: "Summer Camp", "After-School Program",
 *                       "Robotics", "Science", "Coding", "Technology",
 *                       "Engineering", "Math"
 *   item_category2    — location ("Naperville", "Aurora", "Oswego",
 *                       "Plainfield", …) — required for ads/geo reporting
 *   item_variant      — grade-band ("K-2", "3-5", "6-8") when known
 *   price             — per-unit USD price
 *   quantity          — number of units
 *   currency: "USD"
 *
 * Event-level fields always include:
 *   currency: "USD"
 *   value             — total purchase amount (sum of price × quantity)
 */

const CURRENCY = 'USD';

/** Map a raw category value to the human-readable label GA4 reports expect. */
function normalizeCategory(raw) {
  if (!raw) return 'Summer Camp';
  const key = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  const map = {
    summer_camp: 'Summer Camp',
    camp: 'Summer Camp',
    course: 'After-School Program',
    after_school: 'After-School Program',
    after_school_program: 'After-School Program',
    afterschool: 'After-School Program',
    weekend_classes: 'Weekend Class',
    weekend_class: 'Weekend Class',
    robotics: 'Robotics',
    science: 'Science',
    coding: 'Coding',
    technology: 'Technology',
    engineering: 'Engineering',
    math: 'Math',
  };
  // Preserve already-formatted values (e.g. "Summer Camp", "Robotics") so
  // explicit caller-provided labels aren't downgraded.
  if (/^[A-Z]/.test(String(raw)) && !map[key]) return String(raw);
  return map[key] || String(raw);
}

/** Return true iff we have a gtag function to call. */
const hasGtag = () =>
  typeof window !== 'undefined' && typeof window.gtag === 'function';

/** Low-level fire; swallows all errors so analytics never crashes a flow. */
function emit(event, params) {
  if (!hasGtag()) return;
  try {
    window.gtag('event', event, { currency: CURRENCY, ...params });
  } catch {
    // Intentionally ignored — analytics must never break the UX.
  }
}

/**
 * Normalise a camp / cart item into GA4's items[] entry shape.
 * Accepts loose inputs so callers can pass raw API responses.
 */
function toGa4Item(raw, { quantity = 1 } = {}) {
  if (!raw) return null;
  const price = Number(
    raw.price ?? raw.unit_price ?? raw.camp_price ?? raw.total ?? 0,
  );
  const id = raw.item_id ?? raw.id ?? raw.camp_id ?? raw.section_id ?? raw.camp_section_id ?? '';
  const name = raw.item_name ?? raw.name ?? raw.title ?? raw.camp_name ?? '';
  const category = normalizeCategory(raw.item_category ?? raw.category);
  const location =
    raw.item_category2 ?? raw.location ?? raw.location_name ?? raw.section_location ?? null;
  const variant = raw.item_variant ?? raw.grade_band ?? raw.grade_bands?.[0];

  const item = {
    item_id: String(id),
    item_name: String(name),
    item_category: category,
    price: Number.isFinite(price) ? Number(price.toFixed(2)) : 0,
    quantity: Number(quantity) || 1,
    currency: CURRENCY,
  };
  if (location) item.item_category2 = String(location);
  if (variant) item.item_variant = String(variant);
  return item;
}

/** Sum item prices × quantities — GA4 expects `value` to be a number. */
function sumValue(items) {
  return Number(
    items
      .reduce((s, it) => s + Number(it.price || 0) * Number(it.quantity || 1), 0)
      .toFixed(2),
  );
}

/** Fired when a user opens a camp / course detail page. */
export function trackViewItem(item) {
  const ga4 = toGa4Item(item);
  if (!ga4) return;
  emit('view_item', { value: sumValue([ga4]), items: [ga4] });
}

/** Fired on a successful POST /api/camps/<id>/add-to-cart/. */
export function trackAddToCart(item, { quantity = 1 } = {}) {
  const ga4 = toGa4Item(item, { quantity });
  if (!ga4) return;
  emit('add_to_cart', { value: sumValue([ga4]), items: [ga4] });
}

/** Fired when a cart row is removed. */
export function trackRemoveFromCart(item, { quantity = 1 } = {}) {
  const ga4 = toGa4Item(item, { quantity });
  if (!ga4) return;
  emit('remove_from_cart', { value: sumValue([ga4]), items: [ga4] });
}

/** Fired when the user clicks the Checkout button in the Cart. */
export function trackBeginCheckout(items) {
  const ga4Items = (items || [])
    .map(i => toGa4Item(i, { quantity: i?.quantity ?? 1 }))
    .filter(Boolean);
  if (ga4Items.length === 0) return;
  emit('begin_checkout', { value: sumValue(ga4Items), items: ga4Items });
}

/**
 * Fired once per successful Stripe checkout on /checkout/success.
 *
 * Idempotency: GA4 treats duplicate `transaction_id`s as new revenue. React
 * strict-mode double-mounts + browser refreshes would double-count, so we
 * stamp the transaction_id in sessionStorage and bail on repeats.
 */
export function trackPurchase({
  transactionId,
  value,
  tax = 0,
  shipping = 0,
  items = [],
}) {
  if (!transactionId) return;
  if (typeof window === 'undefined') return;

  const storageKey = `ga4_purchase_fired_${transactionId}`;
  try {
    if (window.sessionStorage?.getItem(storageKey)) return;
  } catch {
    // sessionStorage unavailable — continue, worst case we double-fire once
  }

  const ga4Items = (items || [])
    .map(i => toGa4Item(i, { quantity: i?.quantity ?? 1 }))
    .filter(Boolean);
  emit('purchase', {
    transaction_id: String(transactionId),
    value: Number(value ?? sumValue(ga4Items)),
    tax: Number(tax || 0),
    shipping: Number(shipping || 0),
    items: ga4Items,
  });

  try {
    window.sessionStorage?.setItem(storageKey, '1');
  } catch {
    // ignore
  }
}
